// =========================================================================
// FETCH CON REINTENTO + BACKOFF EXPONENCIAL
// -------------------------------------------------------------------------
// Por qué existe:
//   En la primera prueba en device real con Wi-Fi inestable, login y subida
//   de KYC fallaban con `java.net.ConnectException` aunque el backend SÍ
//   estuviera vivo. Mobile data 5G/LTE casi nunca falla; el Wi-Fi residencial
//   (especialmente con MTU 1492 de PPPoE, DNS de operador, IPv6 a medias,
//   captive portal restos) DROPea paquetes y mata el TCP handshake del
//   primer intento. Uber, WhatsApp y compañía sobreviven a eso reintentando
//   silenciosamente — el user nunca se entera.
//
// Política:
//   - Reintenta SOLO errores transitorios:
//       * TypeError / "Network request failed" / fetch failed / ECONNRESET
//       * HTTP 408 (Request Timeout), 429 (Too Many Requests),
//         500/502/503/504 (Bad Gateway, Service Unavailable, Gateway Timeout)
//   - NO reintenta 4xx (auth/wrong-password, validation, etc.) — son del
//     cliente y reintentar solo enmascara el bug.
//   - Backoff exponencial con jitter para no sincronizar reintentos cuando
//     un backend se cae y miles de devices reintentan al mismo segundo
//     (el "thundering herd"). Base 1s → 2s → 4s, +/- 30% random.
//   - Max 3 intentos (el original + 2 reintentos). 7 segundos de espera
//     total en el peor caso antes de fallar.
// =========================================================================

export type RetryFetchOptions = RequestInit & {
  /** Max intentos totales incluyendo el primero. Default 3. */
  retries?: number;
  /** Delay base en ms antes del segundo intento. Default 1000. */
  baseDelayMs?: number;
  /** Timeout por intento individual en ms. Default 25000. */
  perAttemptTimeoutMs?: number;
  /** Logger opcional para debug. Si se omite, usamos console.warn. */
  onRetry?: (attempt: number, reason: string) => void;
};

const DEFAULT_RETRIES = 3;
const DEFAULT_BASE_DELAY = 1000;
const DEFAULT_TIMEOUT = 25_000;

/** Códigos HTTP que vale la pena reintentar. */
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

/** Mensajes de error de red que indican "no llegó al server" — siempre retry. */
const NETWORK_ERROR_HINTS = [
  'network request failed',
  'fetch failed',
  'failed to fetch',
  'connect',         // java.net.ConnectException
  'econnreset',
  'econnrefused',
  'etimedout',
  'enetunreach',
  'eai_again',       // DNS retry
  'socket hang up',
  'tls',
  'ssl',
];

function isRetryableNetworkError(err: any): boolean {
  if (!err) return false;
  const msg = String(err?.message ?? err ?? '').toLowerCase();
  if (err?.name === 'AbortError') return false; // abort intencional → NO retry
  return NETWORK_ERROR_HINTS.some((h) => msg.includes(h));
}

function jitteredDelay(attempt: number, base: number): number {
  const exp = base * Math.pow(2, attempt - 1); // 1, 2, 4...
  const jitter = exp * (Math.random() * 0.6 - 0.3); // ±30%
  return Math.max(100, Math.round(exp + jitter));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * fetch con reintentos. Devuelve la Response del intento exitoso (que puede
 * tener cualquier status 2xx/3xx/4xx no-retryable). Lanza el error del
 * último intento si TODOS fallan con error de red, o devuelve la última
 * Response si todos los reintentos terminan con un status retryable —
 * el caller decide qué hacer con el body en ese caso.
 *
 * Diseño deliberado: NO lanzamos en respuestas 5xx después de agotar
 * reintentos. El caller que ya estaba leyendo `resp.ok` sigue funcionando
 * sin cambios y muestra el mensaje del server (que probablemente diga
 * "estamos teniendo problemas, intenta más tarde").
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  init: RetryFetchOptions = {}
): Promise<Response> {
  const {
    retries = DEFAULT_RETRIES,
    baseDelayMs = DEFAULT_BASE_DELAY,
    perAttemptTimeoutMs = DEFAULT_TIMEOUT,
    onRetry,
    signal: externalSignal,
    ...fetchInit
  } = init;

  let lastError: any = null;
  let lastResponse: Response | null = null;

  for (let attempt = 1; attempt <= retries; attempt++) {
    // Cada intento tiene su propio AbortController para timeout independiente.
    // Si el caller pasó su propio signal (cancelación externa), lo
    // observamos para abortar también nuestro controller.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), perAttemptTimeoutMs);
    const onExternalAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort();
      } else {
        externalSignal.addEventListener('abort', onExternalAbort, { once: true });
      }
    }

    try {
      const resp = await fetch(input, { ...fetchInit, signal: controller.signal });
      clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);

      if (resp.ok || !RETRYABLE_STATUS.has(resp.status)) {
        // 2xx/3xx → éxito. 4xx no-retryable (401, 403, 404, etc.) → tampoco
        // tiene sentido reintentar, devolvemos para que el caller maneje.
        return resp;
      }
      // 5xx / 408 / 429 — vale la pena reintentar.
      lastResponse = resp;
      lastError = null;
      const reason = `HTTP ${resp.status}`;
      (onRetry ?? defaultOnRetry)(attempt, reason);
    } catch (e: any) {
      clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);

      // Si el aborto vino del signal externo, propagamos sin retry.
      if (externalSignal?.aborted) throw e;

      // Timeout del intento (nuestro AbortController) o error de red.
      const isTimeout = e?.name === 'AbortError';
      const isNetwork = isTimeout || isRetryableNetworkError(e);

      if (!isNetwork) {
        throw e; // error no recuperable, no reintentar
      }

      lastError = e;
      lastResponse = null;
      const reason = isTimeout ? `timeout ${perAttemptTimeoutMs}ms` : (e?.message ?? 'network');
      (onRetry ?? defaultOnRetry)(attempt, reason);
    }

    // Si todavía nos quedan intentos, esperar con backoff antes del siguiente.
    if (attempt < retries) {
      const delay = jitteredDelay(attempt, baseDelayMs);
      await sleep(delay);
    }
  }

  // Agotamos reintentos.
  if (lastResponse) return lastResponse; // último 5xx
  throw lastError ?? new Error('fetchWithRetry: all attempts failed');
}

function defaultOnRetry(attempt: number, reason: string) {
  console.warn(`[fetchWithRetry] intento ${attempt} falló (${reason}), reintentando...`);
}
